import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const PREDICTIVE_SEARCH_TIMEOUT_MS = 5000;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateMarkup = '';

  #historyRestored = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller.abort();
    this.#controller = new AbortController();
    if (!this.#emptyStateMarkup) this.#emptyStateMarkup = this.refs.predictiveSearchResults.innerHTML;

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }
    this.#syncResultsAccessibility();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
      window.addEventListener('pageshow', this.#restoreSearchContext, { signal });
      this.#restoreSearchContext();
    }

  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const retrySearchTrigger = target.closest('[data-vs-search-retry]');
    if (retrySearchTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      // Retry the current field, not text captured by an older failed request.
      this.#runQuickSearch(this.refs.searchInput.value);
      return;
    }
    const quickSearchTrigger = target.closest('[data-vs-search-query]');

    if (quickSearchTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#runQuickSearch(quickSearchTrigger.dataset.vsSearchQuery);
      return;
    }

    const productLink = target.closest('a[href]');
    if (productLink instanceof HTMLAnchorElement) this.#rememberSearchContext(event, productLink);

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    this.#controller.abort();
    this.#abortActiveFetch();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
    this.#forgetSearchContext();
  };

  #rememberSearchContext(event, link) {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    const query = this.refs.searchInput.value.trim();
    if (!query || query.length > 200) return;
    try {
      const url = new URL(link.href, location.origin);
      if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) return;
      const state = window.history.state;
      // Do not overwrite another integration's non-object history state.
      if (state !== null && (typeof state !== 'object' || Array.isArray(state))) return;
      window.history.replaceState({ ...state, vsPredictiveSearch: {
        query,
        source: location.pathname + location.search,
        scrollTop: Math.max(0, this.refs.predictiveSearchResults.scrollTop || 0),
      } }, '');
      this.#historyRestored = false;
    } catch {
      // A denied history write must never block the ordinary product link.
    }
  }

  #forgetSearchContext() {
    try {
      const state = window.history.state;
      if (!state?.vsPredictiveSearch) return;
      const { vsPredictiveSearch, ...otherState } = state;
      window.history.replaceState(otherState, '');
    } catch {}
  }

  #restoreSearchContext = async (event) => {
    if (this.#historyRestored || !this.isConnected) return;
    const backForward = event?.persisted || window.performance?.getEntriesByType('navigation')[0]?.type === 'back_forward';
    if (!backForward) return;
    let saved;
    try { saved = window.history.state?.vsPredictiveSearch; } catch { return; }
    if (!saved || saved.source !== location.pathname + location.search || typeof saved.query !== 'string') return;
    const query = saved.query.trim();
    if (!query || query.length > 200 || !this.dialog) return;
    this.#historyRestored = true;
    this.dialog.showDialog();
    this.refs.searchInput.value = query;
    this.#showResetButton();
    // Refetch instead of restoring old HTML with potentially stale stock/prices.
    await this.#getSearchResults(query);
    // Run after the normal search result scroll reset has completed its frame.
    await new Promise(resolve => requestAnimationFrame(resolve));
    if (!this.isConnected || this.refs.searchInput.value.trim() !== query) return;
    if (Number.isFinite(saved.scrollTop) && saved.scrollTop >= 0) {
      this.refs.predictiveSearchResults.scrollTo({ top: saved.scrollTop, behavior: 'instant' });
    }
  };

  #handleDialogOpen = () => {
    this.refs.searchInput.focus();
    window.VSCRO?.track('search_open', { source: 'predictive_search' });
    window.VSCRO?.track('witek_open', { source: 'predictive_search' });
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ ([...new Set(allItems)]);
  }

  #syncResultsAccessibility(empty = false) {
    const { searchInput, predictiveSearchResults } = this.refs;
    const popup = predictiveSearchResults.querySelector('#predictive-search-results');
    const items = empty ? [] : this.#allResultsItems;
    searchInput.setAttribute('aria-expanded', String(items.length > 0));
    searchInput.removeAttribute('aria-activedescendant');
    if (!popup) return;
    const status = popup.querySelector('[role="status"]');
    if (status) status.textContent = empty ? '' : `Dostępne wyniki: ${items.length}.`;
    popup.removeAttribute('aria-expanded');
    popup.setAttribute('role', items.length ? 'listbox' : 'region');
    popup.setAttribute('aria-label', 'Wyniki wyszukiwania');
    popup.querySelectorAll('ul').forEach(group => group.setAttribute('role', 'group'));
    items.forEach((item, index) => {
      item.id = `${searchInput.id}-option-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      // The combobox owns arrow-key selection; Tab moves to form controls.
      item.querySelectorAll('a, button').forEach(link => link.setAttribute('tabindex', '-1'));
    });
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    this.refs.searchInput.removeAttribute('aria-activedescendant');
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    if (activeItem) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput || event.isComposing) return;
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      this.#currentIndex = -1;
      return;
    }

    if (!this.#allResultsItems?.length || this.refs.searchInput.getAttribute('aria-expanded') !== 'true') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        // The native GET form owns submission; its submit event owns analytics.
        break;
      }
    }
  };

  /**
   * Runs a popular search without navigating away from the modal.
   * Delegation keeps the chips functional after predictive-result morphs.
   * @param {string | undefined} rawQuery
   */
  #runQuickSearch(rawQuery) {
    const query = rawQuery?.trim();
    if (!query) return;
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    this.refs.searchInput.value = query;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.refs.searchInput.focus();
    this.#currentIndex = -1;
    this.#showResetButton();
    this.#getSearchResults(query);
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = (event) => {
    // InputEvent.inputType is not supplied by every browser/input method.
    if (event.type !== 'input') return;
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    this.#abortActiveFetch();
    this.#currentIndex = -1;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-loading');
    this.refs.searchInput.removeAttribute('aria-busy');
    if (event.isComposing) return;
    this.#debouncedSearch();
  };

  #debouncedSearch = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 160);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();
    let didTimeout = false;
    const timeoutId = window.setTimeout(() => {
      if (this.#activeFetch !== abortController) return;
      didTimeout = true;
      abortController.abort();
    }, PREDICTIVE_SEARCH_TIMEOUT_MS);
    this.toggleAttribute('data-loading', true);
    this.refs.searchInput.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[limit_scope]', 'each');

      const resultsMarkup = await this.#getNativeResults(url, abortController.signal);
      if (abortController.signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) return;
      morph(predictiveSearchResults, resultsMarkup);
      this.#syncResultsAccessibility();
      if (predictiveSearchResults.querySelector('.predictive-search-results__no-results')) {
        window.VSCRO?.track('search_zero', {
          ...window.VSCRO.queryShape(searchTerm),
          source: 'predictive_search',
          active_filter_count: 0,
          fallback_shown: Boolean(predictiveSearchResults.querySelector('.predictive-search-results__products')),
        });
      }
      this.#resetScrollPositions();
    } catch (error) {
      if (this.#activeFetch !== abortController || this.refs.searchInput.value.trim() !== searchTerm) return;
      if (abortController.signal.aborted && !didTimeout) return;
      if (!abortController.signal.aborted) abortController.abort();
      const message = document.createElement('div');
      message.id = 'predictive-search-results';
      message.className = 'predictive-search-dropdown';
      message.setAttribute('role', 'status');
      message.innerHTML =
        `<div class="predictive-search-results__inner"><p class="predictive-search-results__no-results">${
          didTimeout ? 'Wyszukiwanie trwało zbyt długo. Spróbuj ponownie.' : 'Nie udało się pobrać wyników. Spróbuj ponownie.'
        }</p><button type="button" class="predictive-search__retry" data-vs-search-retry>Spróbuj ponownie</button></div>`;
      predictiveSearchResults.replaceChildren(message);
      console.warn('Vintage Szafa search failed', error);
      window.VSCRO?.track('ui_error', {
        component: 'predictive_search',
        error_code: didTimeout ? 'search_request_timeout' : 'search_request_failed',
        action: 'search',
      });
    } finally {
      window.clearTimeout(timeoutId);
      if (this.#activeFetch === abortController) {
        this.removeAttribute('data-loading');
        this.refs.searchInput.removeAttribute('aria-busy');
      }
    }
  }

  async #getNativeResults(url, signal) {
    const requestUrl = new URL(url.toString());
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);
    requestUrl.searchParams.sort();
    const response = await fetch(requestUrl.toString(), {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`predictive_search_http_${response.status}`);
    return response.text();
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    this.#abortActiveFetch();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #abortActiveFetch() {
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  #resetSearch = async () => {
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.value = '';
    this.#hideResetButton();

    this.#abortActiveFetch();
    this.removeAttribute('data-loading');
    searchInput.removeAttribute('aria-busy');
    if (!this.#emptyStateMarkup) return;

    const template = document.createElement('template');
    template.innerHTML = this.#emptyStateMarkup;
    predictiveSearchResults.replaceChildren(template.content.cloneNode(true));
    this.#syncResultsAccessibility(true);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
